import { useEffect, useRef } from 'react'
import { MuseMidiDeviceInfo } from '@/services/MusePeer'
import { PeerMidiActivityEventEmitter } from '@/services/PeerMidiActivityEventEmitter'
import { MIDIInput } from '@/types/WebMidi'
import { cn } from '@/utlis/cn'

const COLOR_OFF = 'bg-gray-500'
const METER_COLOR_ACTIVE = 'bg-[#fb3327]'
const METER_COLOR = 'bg-[#aa372f]'
const METER_BASE_STYLES = ['h-[8px] w-[8px] bg-red-600 rounded-[2px]']

const containerClasses =
    'shadow-glow border-muse-toplit-low rounded-[12px] flex items-center gap-[6px] px-[16px] py-[8px]'

type MidiMeterProps = {
    midiInput: MIDIInput | null
    className?: string
}
export function MidiMeter({ midiInput, className }: MidiMeterProps) {
    const meterRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null

        const handler = (ev: any) => {
            const { data } = ev

            // filter out SysEx messages, ActiveSensing, and other undesired messages.
            // Note: Its effect is not completely understood.
            if ((data[0] & 0xf0) === 0xf0) {
                return
            }

            console.log('MidiMeter: midimessage event.')

            // const { data } = ev
            if (meterRef.current) {
                meterRef.current.className = cn(METER_BASE_STYLES, METER_COLOR_ACTIVE)
            }
            if (timer) {
                clearTimeout(timer)
            }
            timer = setTimeout(() => {
                if (meterRef.current) {
                    meterRef.current.className = cn(METER_BASE_STYLES, METER_COLOR)
                }
                timer = null
            }, 100)
        }

        if (midiInput) {
            console.log('MidiMeter: adding event listener.')
            midiInput.addEventListener('midimessage', handler)
        }

        const cleanup = () => {
            console.log('MidiMeter cleanup: midiInput=%O', midiInput)
            midiInput && midiInput.removeEventListener('midimessage', handler)
        }

        return () => {
            cleanup()
        }
    }, [midiInput])

    return (
        <div className={cn(containerClasses, className)}>
            <div ref={meterRef} className={cn(METER_BASE_STYLES, METER_COLOR)} />
            <span className="text-muse-fg/65 font-light">MIDI</span>
        </div>
    )
}

export function PeerMidiMeter({
    peerMidiDevice,
    className,
}: {
    peerMidiDevice: MuseMidiDeviceInfo
    className?: string
}) {
    const meterRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        let timeout: NodeJS.Timeout | undefined = undefined
        const handler = (deviceId: string, data: number[]) => {
            if (peerMidiDevice && peerMidiDevice.sourceId === deviceId) {
                if ((data[0] & 0xf0) === 0xf0) {
                    // no-op
                } else {
                    if (meterRef.current) {
                        meterRef.current.className = cn(METER_BASE_STYLES, METER_COLOR_ACTIVE)
                        clearTimeout(timeout)
                        timeout = setTimeout(() => {
                            if (meterRef.current) {
                                meterRef.current.className = cn(METER_BASE_STYLES, METER_COLOR)
                            }
                        }, 500)
                    }
                }
            }
        }

        PeerMidiActivityEventEmitter.addListener('mididata', handler)
        return () => {
            PeerMidiActivityEventEmitter.removeListener('mididata', handler)
        }
    }, [peerMidiDevice])

    return (
        <div className={cn(containerClasses, className)}>
            <div ref={meterRef} className={cn(METER_BASE_STYLES, METER_COLOR)} />
            <span className="text-muse-fg/65 font-light">MIDI</span>
        </div>
    )
}
